import { resolveBizAction } from '@/constants/bizCode';
import { menuKeysOfRoutes } from '@/constants/menuCatalog';
import { translate } from '@/i18n/runtime';
import type { BackofficeLoginParams } from '@/services/auth';
import { getIamSession, iamLogin, iamLogout } from '@/services/iam-admin';
import type { AdminLoginResp, IamSessionResp } from '@/types/iam-admin';
import { useModuleGateStore } from '@/store/moduleGate';
import { feedback } from '@/utils/feedback';
import { bizCodeOf } from '@/utils/http';
import {
  establishSession,
  invalidateSession,
  onSessionInvalidated,
  sessionRevision,
} from '@/utils/sessionLifecycle';
import { create } from 'zustand';

export type AuthStatus = 'idle' | 'loading' | 'authed' | 'anon' | 'error';

/**
 * 登录态。**只有 iam 一套会话**（IAM-only）：浏览器里的凭据是服务端下发的 HttpOnly Cookie，
 * JS 读不到也写不了，所以「我是谁、我能进哪些页」每次启动都只能现问一次服务端，没有本地副本可恢复。
 *
 * 三个接口，各管一段，互不交叉：登录 {@link AuthState.loginBackoffice}（iam 的 131）、
 * 启动/刷新 {@link AuthState.bootstrap}（iam 的 132）、退出 {@link AuthState.signOut}（iam 的登出）。
 * 旧单体的 login / logout / auth / me / permissions 一个都不再调——它们的调用点在 `utils/http.ts`
 * 的统一出口被拒（见那里的 `request`）。
 */
interface AuthState {
  /**
   * idle=还没问过；loading=正在问；authed=已登录；anon=未登录；
   * error=问了但没问出结论（断网、5xx、代理没起——不是 401），会话可能还好着，由 AuthGuard 给重试出口
   */
  status: AuthStatus;
  /** 顶栏称呼只采用正式登录/会话出参，不从邮箱猜账户名称。 */
  accountName: string | null;
  /** 当前会话机构对外名称，不保留上一租户名称或替代品牌。 */
  tenantName: string | null;
  /**
   * 能进哪些页（菜单键）：服务端 `reachableRoutes` 按 `menuCatalog.MENU_UI` 映射来的。
   *
   * 普通帐号到这里为止；超管的「全都进得去」**不在这里展开**，由 `useAccess` / `useMenu`
   * 各按 `superAdmin` 通配一次——两处都只认服务端那个布尔，前端不自己推。
   */
  menus: Set<string>;
  /**
   * 服务端判定的「当下有效的内置超管」（内置超管岗位 ＋ 双通配策略行）：菜单与按钮一律全通。
   *
   * 🔴 只认登录（131）与当前会话（132）回的这一个布尔——**不认登录邮箱、不认 pageCount /
   * dataScope，也不记本地标志**。那些都不是「有效内置超管」的判据，推出来的结论会与网关
   * 逐条判按钮的结果对不上。
   */
  superAdmin: boolean;
  /**
   * 这个帐号当下持有的**动作码**集合（`org.notice.draft` 这类点分码），按钮级判定读它。
   *
   * 登录与刷新都从服务端那一格落下（缺了按空数组兜底）；退出与失效一起清空，
   * 不留上一个人的按钮权限。超管的「全通」不在这里展开，仍由 `useAccess` 按 superAdmin 通配。
   */
  permissions: Set<string>;
  /**
   * 数据范围，服务端 `DataScope` 的 code（all / dept / self）。
   *
   * 只有当前会话（132）回这一格，登录（131）没回：刚登录时为空串，刷新后才有值。
   * `DataScopeHint` 认不出的值不渲染，免得替服务端编一档出来。
   */
  scope: string;
  /** 问一次服务端「我登录了吗、能进哪些页」。凭据在 HttpOnly Cookie 里，JS 读不到，只能问 */
  bootstrap: () => Promise<void>;
  /**
   * 心跳用的刷新：登录态已是 authed 时问一次当前会话，成功就整份落下（顺带续会话、刷新菜单与按钮权限）。
   *
   * 与 {@link bootstrap} 的差别只有两点：不把 status 拨成 loading（界面不闪进行中态），
   * 失败也不改登录态——401 由统一出口按「用着用着被踢」跳登录，断网、5xx 静默等下一次业务请求。
   */
  refresh: () => Promise<void>;
  /**
   * 后台登录的**唯一入口**，也只调 iam 的登录（131）：
   *
   * - 机构编码一起带上：网关按它定位租户、签上下文（不按访问域名——本机开后台时 Host 是
   *   localhost，域名定不出机构）；iam 拿它与上下文比对，对不上按凭据不对处理。
   * - 没有降级路径。iam 说凭据不对 / 停用 / 锁定 / 机构冻结 / 限流 / 连不上，都原样抛给调用方
   *   显示在表单上，不再拿同一组密码去问旧单体（那只是把同一个错误重复记一次失败次数），
   *   也不把「已停用」的人从别的路放进来。
   * - 成功后整份登录态以它的出参为准（`reachableRoutes` 定菜单、`superAdmin` 定通配、
   *   `permissions` 定按钮）。
   *
   * 落地页由调用方决定（`Login` 读 `menuEmpty` 与 `reachableRoutes`，不改写服务端算出来的那一份）。
   */
  loginBackoffice: (params: BackofficeLoginParams) => Promise<AdminLoginResp>;
  /**
   * 退出：只撤 iam 会话。本地态无论成败都清——接口失败也让界面回到未登录，
   * 但会明确提示「没退干净」（Cookie 还在，下一个打开后台的人会被认成这个人）。
   */
  signOut: () => Promise<void>;
}

const EMPTY = {
  accountName: null,
  tenantName: null,
  menus: new Set<string>(),
  superAdmin: false,
  permissions: new Set<string>(),
  scope: '',
};
let identityRequest = 0;

/** 出错的 HTTP 状态码；没收到响应（断网、超时）时 undefined */
const httpStatusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } } | null | undefined)?.response?.status;

/**
 * 服务端那几格权限（登录与当前会话同源）→ 整份登录态。
 *
 * 两个出参都带 `reachableRoutes` / `superAdmin` / `permissions`，所以这里能一处落定：
 * 刷新后不会出现「登录说能进、刷新说不能」这种只在界面上少几颗菜单的偏差。
 */
const authedOf = (resp: AdminLoginResp | IamSessionResp) => ({
  status: 'authed' as const,
  accountName: resp.accountName,
  tenantName: resp.tenantName,
  menus: new Set(menuKeysOfRoutes(resp.reachableRoutes)),
  superAdmin: resp.superAdmin,
  // 下发里没有这一格（旧版服务端）时按「一个动作都没有」处理，按钮全灰但页面不崩
  permissions: new Set(resp.permissions ?? []),
  // 登录出参没有数据范围那一格（只有当前会话有）：先留空串，刷新后由 132 补上
  scope: 'dataScope' in resp ? resp.dataScope : '',
});

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'idle',
  ...EMPTY,

  bootstrap: async () => {
    if (get().status === 'loading') return;
    set({ status: 'loading' });
    const revision = sessionRevision();
    const request = ++identityRequest;
    try {
      // skipErrorHandler：这一问纯粹是「我登录了吗」，401 是意料之中的正常结果（包括从没登录过），
      // 不该弹全局 toast、更不该带着 reason=expired 跳登录页——那条 reason 留给「用着用着被踢下线」
      const session = await getIamSession({
        skipErrorHandler: true,
        sessionFailure: 'local',
      });
      if (revision !== sessionRevision() || request !== identityRequest) return;
      establishSession();
      set(authedOf(session));
    } catch (error) {
      if (revision !== sessionRevision() || request !== identityRequest) return;
      const action = resolveBizAction(bizCodeOf(error), httpStatusOf(error));
      if (action.redirect === '/login') {
        // 首次探测由路由守卫展示登录入口，不把正常未登录伪装成使用中失效。
        set({ status: 'anon', ...EMPTY });
        return;
      }
      // 没有响应、5xx 等：会话可能还好着，踢回登录页会让人以为被登出了，而重新登录解决不了断网
      set({ status: 'error', ...EMPTY });
    }
  },

  refresh: async () => {
    // 只在已登录时跑：心跳不该把「还没登录」的一问变成一次跳登录
    if (get().status !== 'authed') return;
    const revision = sessionRevision();
    const request = ++identityRequest;
    try {
      // 不带 skipErrorHandler：被踢（401）要走统一出口那条「用着用着失效」的跳登录
      const session = await getIamSession();
      // 期间被登录、退出或失效抢先时，这份晚到的出参不能覆盖当下的登录态
      if (revision !== sessionRevision() || request !== identityRequest) return;
      if (get().status === 'authed') set(authedOf(session));
    } catch {
      // 断网、5xx 不动登录态：心跳失败不该把人踢下线；401 已在统一出口处理
    }
  },

  loginBackoffice: async ({ tenantCode, email, password }) => {
    const revision = sessionRevision();
    const request = ++identityRequest;
    // skipErrorHandler：登录失败要显示在表单上，不能被全局处理跳走
    let resp: AdminLoginResp;
    try {
      resp = await iamLogin(
        { tenantCode, loginId: email, password },
        { skipErrorHandler: true },
      );
    } catch (error) {
      // 登录已接管旧探测的身份写权；失败后不能把 loading 留给已作废的探测收尾。
      if (
        revision === sessionRevision() &&
        request === identityRequest &&
        get().status === 'loading'
      ) {
        set({ status: 'anon', ...EMPTY });
      }
      throw error;
    }
    if (revision !== sessionRevision() || request !== identityRequest) {
      throw new Error(translate('http.expired'));
    }
    establishSession();
    set(authedOf(resp));
    return resp;
  },

  signOut: async () => {
    // 即使本轮已失效，退出也要作废失效之后发起、尚未返回的登录。
    establishSession();
    invalidateSession();
    set({ status: 'anon', ...EMPTY });
    // 模块门控跟着会话走：不清的话下一个人进来会先看到上一个人的档位
    useModuleGateStore.getState().reset();
    try {
      // skipErrorHandler：退出按钮自己会说，别弹两遍；没有会话时服务端回 401 也算意料之中
      await iamLogout({ skipErrorHandler: true });
    } catch (error) {
      // 撤不掉（断网、限流…）：Cookie 还在，下一个打开后台的人会被认成这个人。不挡退出，
      // 但要让他知道没退干净——不管服务端说了什么，这里要说的都是这一句。
      // 401 除外：那说明本来就没有会话，要撤的东西不在，与撤干净同一档
      if (resolveBizAction(bizCodeOf(error), httpStatusOf(error)).redirect !== '/login') {
        feedback.message.error(translate('layout.logoutIncomplete'));
      }
    }
  },
}));

onSessionInvalidated(() => {
  useAuthStore.setState({ status: 'anon', ...EMPTY });
  useModuleGateStore.getState().reset();
});
